import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';

interface TeacherData {
  _id: string;
  id: string;
  user_id: string;
  name: string;
  gender: string | null;
  email_id: string;
  parentage: string | null;
  dob: string | null;
  address: string | null;
  contact_no: string;
  account_no: string | null;
  bank_name: string | null;
  ifsc_code: string | null;
  created_at: string;
  updated_at: string;
  profile_picture_url: string | null;
  user_status: number | null;
  user_login: string | null;
  status: string;
  qualification: string | null;
  marital_status: string | null;
  joining_date: string | null;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

interface GenderStats {
  counts: { Male: number; Female: number; Other: number };
  total: number;
  percentages: { Male: number; Female: number; Other: number };
}

const TeacherCredentialsGenerator: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [genderStats, setGenderStats] = useState<GenderStats | null>(null);

  // Filters
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  // PDF settings
  const [password, setPassword] = useState<string>('');
  const [websiteLink, setWebsiteLink] = useState<string>(
    'https://play.google.com/store/apps/details?id=com.skoolpillar.app&hl=en'
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchTeachers(1);
  }, [selectedGender, search]);

  const fetchTeachers = async (page: number = 1): Promise<void> => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        limit: 100,
      };
      if (selectedGender) params.gender = selectedGender;
      if (search.trim()) params.search = search.trim();

      const response = await apiClient.get('/teachers', { params });
      const data = response.data;

      if (data.status === 'success') {
        setTeachers(data.data || []);
        setPagination(data.pagination);
        setGenderStats(data.gender_statistics);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: data.message || 'Failed to fetch teachers',
          life: 4000,
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch teachers:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching teachers',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCredentialsPDF = () => {
    if (teachers.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Teachers',
        detail: 'No teachers available to generate credentials',
        life: 4000,
      });
      return;
    }

    const genderText = selectedGender ? ` (${selectedGender})` : '';
    const title = `Teacher Credentials${genderText}`;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            @page { size: A4; margin: 0.3in; }
            @media print {
              body { margin: 0; font-family: Arial, sans-serif; color: #1e40af; background: white; }
              .no-print { display: none !important; }
              .page-break { page-break-before: always; }
            }

            body { font-family: Arial, sans-serif; margin: 8px; background: white; color: #1e40af; font-size: 12px; line-height: 1.3; }
            :root { --gap: 8px; }
            .controls { margin-bottom: 10px; text-align: center; }
            .controls button { background: #1e40af; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; margin: 0 5px; }
            .controls button.close-btn { background: #dc2626; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1e40af; padding-bottom: 8px; }
            .header h1 { color: #1e40af; margin: 0; font-size: 18px; font-weight: bold; }
            .header .meta { color: #666; font-size: 11px; margin: 2px 0; }

            .page { margin-bottom: 20px; position: relative; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(4, 1fr); gap: var(--gap); height: 1000px; }

            .card {
              padding: 10px;
              border: 2px solid #1e40af;
              border-radius: 6px;
              background: white;
              height: 230px;
              position: relative;
              overflow: hidden;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
            }
            .card-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 5px;
              flex-shrink: 0;
            }
            .profile-section {
              display: flex;
              align-items: center;
              flex: 1;
              min-width: 0;
              overflow: hidden;
            }
            .profile-pic, .profile-placeholder {
              width: 52px;
              height: 52px;
              min-width: 52px;
              border: 2px solid #1e40af;
              border-radius: 6px;
              margin-right: 7px;
            }
            .profile-pic { object-fit: cover; }
            .profile-placeholder {
              background: #f8fafc;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              font-weight: bold;
              color: #1e40af;
            }
            .teacher-name {
              font-size: 13px;
              font-weight: bold;
              color: #1e40af;
              margin: 0;
              line-height: 1.3;
              word-break: break-word;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            .gender-badge {
              font-size: 9px;
              padding: 1px 5px;
              border-radius: 3px;
              font-weight: 600;
              margin-top: 2px;
              display: inline-block;
            }
            .gender-male { background: #dbeafe; color: #1e40af; }
            .gender-female { background: #fce7f3; color: #9d174d; }
            .gender-other { background: #f3e8ff; color: #6b21a8; }
            .qr-code {
              width: 52px;
              height: 52px;
              min-width: 52px;
              border: 2px solid #1e40af;
              border-radius: 4px;
            }
            .details {
              font-size: 11px;
              flex: 1;
              overflow: hidden;
            }
            .details div {
              margin: 2px 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .details label {
              font-weight: 600;
              width: 52px;
              display: inline-block;
              color: #1e40af;
              flex-shrink: 0;
            }
            .details span { color: #1f2937; }
            .credentials {
              border: 1px solid #1e40af;
              padding: 2px 6px;
              border-radius: 4px;
              background: #f8fafc;
              font-weight: 600;
              display: inline-block;
              margin: 1px 0;
              font-size: 11px;
              max-width: calc(100% - 60px);
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              vertical-align: middle;
            }
            .footer { text-align: center; color: #666; font-size: 10px; margin-top: 15px; border-top: 1px solid #1e40af; padding-top: 8px; }
          </style>
        </head>
        <body>
          <div class="no-print controls">
            <button onclick="window.print()">🖨️ Print / Save as PDF</button>
            <button class="close-btn" onclick="window.close()">❌ Close</button>
          </div>

          <div class="header">
            <h1>SKOOLPILLAR MANAGEMENT SYSTEM</h1>
            <div class="meta">${title}</div>
            <div class="meta">Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}</div>
          </div>

          ${(() => {
            let content = '';
            const cardsPerPage = 12;
            const totalPages = Math.ceil(teachers.length / cardsPerPage);

            content += `<div class="page-break"></div>`;

            for (let page = 0; page < totalPages; page++) {
              const startIndex = page * cardsPerPage;
              const endIndex = Math.min(startIndex + cardsPerPage, teachers.length);
              const pageTeachers = teachers.slice(startIndex, endIndex);

              if (page > 0) content += `<div class="page-break"></div>`;

              content += `<div class="page"><div class="grid">`;

              pageTeachers.forEach((teacher) => {
                const genderClass =
                  teacher.gender === 'Female'
                    ? 'gender-female'
                    : teacher.gender === 'Other'
                    ? 'gender-other'
                    : 'gender-male';

                content += `
                  <div class="card">
                    <div class="card-header">
                      <div class="profile-section">
                        ${
                          teacher.profile_picture_url
                            ? `<img src="${teacher.profile_picture_url}" alt="${teacher.name}" class="profile-pic" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                               <div class="profile-placeholder" style="display:none;">${teacher.name.charAt(0)}</div>`
                            : `<div class="profile-placeholder">${teacher.name.charAt(0)}</div>`
                        }
                        <div>
                          <div class="teacher-name">${teacher.name || 'N/A'}</div>
                          ${teacher.gender ? `<span class="gender-badge ${genderClass}">${teacher.gender}</span>` : ''}
                        </div>
                      </div>
                      ${websiteLink ? `<img src="https://api.qrserver.com/v1/create-qr-code/?size=52x52&data=${encodeURIComponent(websiteLink)}" class="qr-code" alt="QR" />` : ''}
                    </div>
                    <div class="details">
                      ${teacher.user_login ? `<div><label>User:</label><span class="credentials">${teacher.user_login}</span></div>` : ''}
                      ${password ? `<div><label>Pass:</label><span class="credentials">${password}</span></div>` : ''}
                      <div><label>Email:</label><span>${teacher.email_id || 'N/A'}</span></div>
                      <div><label>Contact:</label><span>${teacher.contact_no || 'N/A'}</span></div>
                      ${teacher.parentage ? `<div><label>S/D/W of:</label><span>${teacher.parentage}</span></div>` : ''}
                      ${teacher.address ? `<div><label>Address:</label><span>${teacher.address}</span></div>` : ''}
                    </div>
                  </div>
                `;
              });

              const remainingSlots = cardsPerPage - pageTeachers.length;
              for (let i = 0; i < remainingSlots; i++) {
                content += `<div class="card" style="border: 1px dashed #ccc; background: #f9f9f9;"></div>`;
              }

              content += `</div></div>`;
            }

            return content;
          })()}

          <div class="footer">
            <p><strong>Skoolpillar Management System</strong> | ${teachers.length} teacher credentials generated</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Generated credentials for ${teachers.length} teachers`,
        life: 4000,
      });
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to open print window. Please check your browser settings.',
        life: 5000,
      });
    }
  };

  const genderColor = (gender: string | null) => {
    if (gender === 'Female') return { bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' };
    if (gender === 'Other') return { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' };
    return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* Header */}
        <GlobalHeaderTitle
          title="Teacher Credentials Generator"
          subtitle="Generate printable teacher credentials with login details (3×4 grid)"
          icon="pi pi-id-card"
          buttons={[
            {
              label: 'Generate PDF',
              icon: 'pi pi-file-pdf',
              onClick: generateCredentialsPDF,
              disabled: teachers.length === 0 || loading,
              style: {
                background:
                  teachers.length > 0 && !loading
                    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                    : undefined,
              },
            },
          ]}
        />

        {/* Filters Row */}
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            {/* Search */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '8px',
                  letterSpacing: '0.02em',
                }}
              >
                Search Teachers
              </label>
              <span className="p-input-icon-left" style={{ width: '100%' }}>
                <i className="pi pi-search" />
                <InputText
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, email or contact..."
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                />
              </span>
            </div>
          </div>

          {/* Gender Filter Chips */}
          {genderStats && genderStats.total > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                marginTop: '16px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginRight: '4px' }}>
                Gender:
              </span>
              
              {/* All Chip */}
              <div
                onClick={() => setSelectedGender(null)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  background: selectedGender === null ? '#1e40af' : 'white',
                  border: `1.5px solid ${selectedGender === null ? '#1e40af' : '#e5e7eb'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: selectedGender === null ? 'white' : '#4b5563' 
                }}>
                  All
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: selectedGender === null ? 'white' : '#1e40af',
                    background: selectedGender === null ? 'rgba(255,255,255,0.2)' : '#dbeafe',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    border: selectedGender === null ? '1px solid rgba(255,255,255,0.3)' : '1px solid #93c5fd',
                  }}
                >
                  {genderStats.total}
                </span>
              </div>

              {/* Male Chip */}
              <div
                onClick={() => setSelectedGender(selectedGender === 'Male' ? null : 'Male')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  background: selectedGender === 'Male' ? '#dbeafe' : 'white',
                  border: `1.5px solid ${selectedGender === 'Male' ? '#93c5fd' : '#e5e7eb'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
                  Male
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#1e40af',
                    background: '#dbeafe',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    border: '1px solid #93c5fd',
                  }}
                >
                  {genderStats.counts.Male}
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {genderStats.percentages.Male.toFixed(1)}%
                </span>
              </div>

              {/* Female Chip */}
              <div
                onClick={() => setSelectedGender(selectedGender === 'Female' ? null : 'Female')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  background: selectedGender === 'Female' ? '#fce7f3' : 'white',
                  border: `1.5px solid ${selectedGender === 'Female' ? '#f9a8d4' : '#e5e7eb'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#9d174d' }}>
                  Female
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#9d174d',
                    background: '#fce7f3',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    border: '1px solid #f9a8d4',
                  }}
                >
                  {genderStats.counts.Female}
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {genderStats.percentages.Female.toFixed(1)}%
                </span>
              </div>

              {/* Other Chip */}
              <div
                onClick={() => setSelectedGender(selectedGender === 'Other' ? null : 'Other')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  background: selectedGender === 'Other' ? '#f3e8ff' : 'white',
                  border: `1.5px solid ${selectedGender === 'Other' ? '#d8b4fe' : '#e5e7eb'}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b21a8' }}>
                  Other
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#6b21a8',
                    background: '#f3e8ff',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    border: '1px solid #d8b4fe',
                  }}
                >
                  {genderStats.counts.Other}
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {genderStats.percentages.Other.toFixed(1)}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* PDF Credential Settings */}
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '8px',
                  letterSpacing: '0.02em',
                }}
              >
                Default Password
              </label>
              <InputText
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter default password"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '8px',
                  letterSpacing: '0.02em',
                }}
              >
                Website Link (QR Code)
              </label>
              <InputText
                value={websiteLink}
                onChange={(e) => setWebsiteLink(e.target.value)}
                placeholder="https://school.example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>
        </div>

        {/* Teachers Preview */}
        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {teachers.length > 0 ? (
              <>
                {/* Info Bar */}
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '20px 20px 0 0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background:
                          'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="pi pi-users" style={{ fontSize: '14px', color: '#059669' }}></i>
                    </div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                      {teachers.length} teacher{teachers.length !== 1 ? 's' : ''} ready for
                      credentials
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {pagination && pagination.total_items > teachers.length && (
                      <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>
                        Showing {teachers.length} of {pagination.total_items}
                      </span>
                    )}
                    <div
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        background:
                          'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
                        border: '1px solid rgba(5, 150, 105, 0.15)',
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#059669', fontWeight: '600' }}>
                        {Math.ceil(teachers.length / 12)} page
                        {Math.ceil(teachers.length / 12) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Teachers Grid Preview */}
                <div
                  style={{
                    padding: '20px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '16px',
                    maxHeight: '600px',
                    overflowY: 'auto',
                  }}
                >
                  {teachers.map((teacher) => {
                    const colors = genderColor(teacher.gender);
                    return (
                      <div
                        key={teacher.id}
                        style={{
                          border: '1.5px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '16px',
                          background: '#fafbfc',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px',
                          }}
                        >
                          {teacher.profile_picture_url ? (
                            <img
                              src={teacher.profile_picture_url}
                              alt={teacher.name}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '10px',
                                objectFit: 'cover',
                                border: '2px solid #1e40af',
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                fontWeight: '600',
                                color: 'white',
                              }}
                            >
                              {teacher.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: '600',
                                fontSize: '15px',
                                color: '#1f2937',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {teacher.name}
                            </div>
                            {teacher.gender && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: colors.text,
                                  background: colors.bg,
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  border: `1px solid ${colors.border}`,
                                  display: 'inline-block',
                                  marginTop: '2px',
                                }}
                              >
                                {teacher.gender}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}
                        >
                          {teacher.email_id && (
                            <div>
                              <strong>Email:</strong> {teacher.email_id}
                            </div>
                          )}
                          <div>
                            <strong>Contact:</strong> {teacher.contact_no || 'N/A'}
                          </div>
                          {teacher.parentage && (
                            <div>
                              <strong>S/D/W of:</strong> {teacher.parentage}
                            </div>
                          )}
                          {teacher.user_login && (
                            <div>
                              <strong>Login:</strong>{' '}
                              <span
                                style={{
                                  fontFamily: 'monospace',
                                  background: '#f0f4ff',
                                  padding: '1px 5px',
                                  borderRadius: '4px',
                                  color: '#1e40af',
                                }}
                              >
                                {teacher.user_login}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              /* Empty State */
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  minHeight: '400px',
                }}
              >
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    background:
                      'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                  }}
                >
                  <i
                    className="pi pi-inbox"
                    style={{ fontSize: '25px', color: 'white' }}
                  ></i>
                </div>
                <h3
                  style={{
                    margin: 0,
                    color: '#1e40af',
                    fontSize: '20px',
                    fontWeight: '700',
                    marginBottom: '8px',
                    textAlign: 'center',
                  }}
                >
                  No Teachers Found
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'center',
                    maxWidth: '400px',
                    lineHeight: 1.6,
                  }}
                >
                  {search.trim()
                    ? `No teachers match "${search}". Try a different search term.`
                    : selectedGender
                    ? `No ${selectedGender} teachers found.`
                    : 'No teachers are registered yet.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TeacherCredentialsGenerator;